<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';

  let banks = [];
  let isLoading = true;
  let error = '';
  let successMessage = '';
  
  // Single Entry State
  let isEditing = false;
  let currentId = null;
  let currentName = '';
  let currentNameZh = '';
  let currentNameJa = '';
  let currentKey = '';
  let currentCountry = 'HK';

  // Bulk Upload State
  let selectedFile = null;
  let isUploading = false;

  onMount(fetchBanks);

  async function fetchBanks() {
    isLoading = true;
    try {
      const response = await fetch(getApiUrl('/banks'));
      if (!response.ok) throw new Error('Could not load banks.');
      banks = await response.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function startEdit(bank) {
    isEditing = true;
    currentId = bank._id;
    currentName = bank.name || bank.nameEn || '';
    currentNameZh = bank.nameZh || '';
    currentNameJa = bank.nameJa || '';
    currentKey = bank.key || '';
    currentCountry = bank.country || 'HK';
    successMessage = '';
    error = '';
  }

  function cancelEdit() {
    isEditing = false;
    currentId = null;
    currentName = '';
    currentNameZh = '';
    currentNameJa = '';
    currentKey = '';
    currentCountry = 'HK';
  }

  async function handleSubmit() {
    if (!currentName.trim() || !currentCountry.trim()) {
      error = 'Name and Country are required.';
      return;
    }
    
    const payload = {
      name: currentName.trim(),
      nameEn: currentName.trim(),
      nameZh: currentNameZh.trim() || undefined,
      nameJa: currentNameJa.trim() || undefined,
      key: currentKey.trim() || undefined,
      country: currentCountry.trim().toUpperCase(),
    };

    let url = getApiUrl('/admin/banks');
    let method = 'POST';

    if (isEditing) {
      url = getApiUrl(`/admin/banks/${currentId}`);
      method = 'PUT';
    }

    try {
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const res = await response.json();
        throw new Error(res.error || 'Operation failed.');
      }
      
      successMessage = isEditing ? $t('admin.banks.update_success') : $t('admin.banks.save_success');
      cancelEdit();
      await fetchBanks();
    } catch (e) {
      error = e.message;
    }
  }

  async function handleDelete(id) {
    if (!confirm($t('admin.banks.delete_confirm'))) return;
    try {
      const response = await authFetch(getApiUrl(`/admin/banks/${id}`), { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete.');
      successMessage = $t('admin.banks.delete_success');
      await fetchBanks();
    } catch (e) {
      alert(e.message);
    }
  }

  // Bulk Upload Logic
  function handleFileSelect(e) {
    selectedFile = e.target.files[0];
  }

  async function handleUpload() {
    if (!selectedFile) return;
    isUploading = true;
    error = '';
    successMessage = '';
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await authFetch(getApiUrl('/banks/upload'), { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Bulk upload failed');
      successMessage = $t('admin.banks.upload_success').replace('{count}', result.inserted ?? 0);
      selectedFile = null;
      const fileInput = document.getElementById('csv-upload');
      if (fileInput) fileInput.value = '';
      await fetchBanks();
    } catch (e) {
      error = e.message;
    } finally {
      isUploading = false;
    }
  }
</script>

<div class="maintenance-container">
  <h2>{$t('admin.banks.title')}</h2>
  
  <div class="grid-layout">
    <div class="action-column">
      <!-- Single Entry Form -->
      <div class="card form-section">
        <h3>{isEditing ? $t('admin.banks.edit_bank') : $t('admin.banks.add_new_bank')}</h3>
        <form on:submit|preventDefault={handleSubmit}>
          <div class="form-group">
            <label for="bank-name">{$t('admin.banks.bank_name_en')} *</label>
            <input type="text" id="bank-name" bind:value={currentName} placeholder="e.g. HSBC" required />
          </div>
          <div class="form-group">
            <label for="bank-name-zh">{$t('admin.banks.bank_name_zh')}</label>
            <input type="text" id="bank-name-zh" bind:value={currentNameZh} placeholder="e.g. 滙豐銀行" />
          </div>
          <div class="form-group">
            <label for="bank-name-ja">{$t('admin.banks.bank_name_ja')}</label>
            <input type="text" id="bank-name-ja" bind:value={currentNameJa} placeholder="e.g. HSBC" />
          </div>
          <div class="form-group">
            <label for="bank-key">{$t('admin.banks.bank_key')}</label>
            <input type="text" id="bank-key" bind:value={currentKey} placeholder="e.g. hsbc (optional)" />
          </div>
          <div class="form-group">
            <label for="bank-country">{$t('admin.banks.country')} *</label>
            <input type="text" id="bank-country" bind:value={currentCountry} placeholder="HK" required />
          </div>
          <div class="form-actions">
            {#if isEditing}
              <button type="button" class="secondary" on:click={cancelEdit}>{$t('admin.banks.cancel')}</button>
            {/if}
            <button type="submit" class="primary">{isEditing ? $t('admin.banks.save_changes') : $t('admin.banks.add_bank')}</button>
          </div>
        </form>
      </div>

      <!-- Bulk Upload -->
      <div class="card upload-section">
        <h3>{$t('admin.banks.bulk_upload')}</h3>
        <p class="hint">{$t('admin.banks.bulk_upload_hint')}</p>
        <input type="file" id="csv-upload" accept=".csv" on:change={handleFileSelect} />
        <button class="upload-btn" on:click={handleUpload} disabled={isUploading || !selectedFile}>
          {isUploading ? $t('admin.banks.uploading') : $t('admin.banks.upload_csv')}
        </button>
      </div>
      
      {#if successMessage}<p class="success">{successMessage}</p>{/if}
      {#if error}<p class="error">{error}</p>{/if}
    </div>

    <!-- List -->
    <div class="card list-section">
      <h3>{$t('admin.banks.existing_banks').replace('{count}', banks.length)}</h3>
      {#if isLoading}
        <p>{$t('admin.banks.loading')}</p>
      {:else if banks.length === 0}
        <p>{$t('admin.banks.no_banks')}</p>
      {:else}
        <div class="table-wrapper">
          <table class="banks-table">
            <thead>
              <tr>
                <th>{$t('admin.banks.bank_name_en')}</th>
                <th>{$t('admin.banks.bank_name_zh')}</th>
                <th>{$t('admin.banks.bank_name_ja')}</th>
                <th>{$t('admin.banks.country')}</th>
                <th>{$t('admin.banks.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {#each banks as bank (bank._id)}
                <tr>
                  <td>
                    <span class="main-name">{bank.name || bank.nameEn || ''}</span>
                    {#if bank.key}<small class="key-badge">{bank.key}</small>{/if}
                  </td>
                  <td>{bank.nameZh || '-'}</td>
                  <td>{bank.nameJa || '-'}</td>
                  <td><span class="item-tag">{bank.country}</span></td>
                  <td class="action-cells">
                    <button class="edit-btn" on:click={() => startEdit(bank)}>{$t('admin.banks.edit')}</button>
                    <button class="delete-btn" on:click={() => handleDelete(bank._id)}>{$t('admin.banks.delete')}</button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .maintenance-container { max-width: 1200px; margin: 2rem auto; padding: 0 1rem; }
  .grid-layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
  @media(min-width: 860px) { .grid-layout { grid-template-columns: 1fr 1.6fr; } }
  .card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 5px rgba(0,0,0,0.05); margin-bottom: 1.5rem; }
  h2 { margin-bottom: 1.5rem; color: #333; }
  h3 { margin-top: 0; color: #0056b3; }
  .form-group { margin-bottom: 1rem; text-align: left; }
  label { display: block; margin-bottom: 0.5rem; font-weight: 500; font-size: 0.9rem; }
  input { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
  .form-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem; }
  button { padding: 0.6rem 1.2rem; border: none; border-radius: 4px; cursor: pointer; font-size: 0.95rem; color: white; }
  .primary, .upload-btn { background-color: #007bff; width: 100%; }
  .primary:hover, .upload-btn:hover { background-color: #0056b3; }
  .secondary { background-color: #6c757d; }
  button:disabled { background-color: #aaa; cursor: not-allowed; }
  
  .table-wrapper { max-height: 650px; overflow-y: auto; overflow-x: auto; }
  .banks-table { width: 100%; border-collapse: collapse; text-align: left; font-size: 0.9rem; }
  .banks-table th, .banks-table td { padding: 0.75rem 0.5rem; border-bottom: 1px solid #eee; vertical-align: middle; }
  .banks-table th { background: #f8f9fa; position: sticky; top: 0; font-weight: 600; color: #495057; }
  
  .main-name { font-weight: 600; color: #333; display: block; }
  .key-badge { background: #e9ecef; color: #495057; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; margin-top: 2px; display: inline-block; font-family: monospace; }
  .item-tag { font-size: 0.8rem; background: #e3f2fd; color: #0d47a1; padding: 2px 6px; border-radius: 4px; font-weight: 500; }
  
  .action-cells { display: flex; gap: 0.4rem; }
  .edit-btn { background-color: #ffc107; color: #333; padding: 4px 8px; font-size: 0.8rem; }
  .delete-btn { background-color: #dc3545; padding: 4px 8px; font-size: 0.8rem; }
  
  .success { color: #28a745; text-align: center; margin-top: 1rem; }
  .error { color: #dc3545; text-align: center; margin-top: 1rem; }
  .hint { font-size: 0.85rem; color: #666; margin-bottom: 0.75rem; }
  #csv-upload { margin-bottom: 0.75rem; }
</style>